import React, { useEffect, useMemo, useState } from "react";
import {
  HiOutlineOfficeBuilding,
  HiOutlineTruck,
  HiOutlineCollection,
  HiOutlineCube,
  HiOutlineCash,
  HiOutlineMap,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import { LogTrackingService } from "../../../../services/LogTrackingService";
import { useDispatchBreakdown, formatVolume, formatMoney } from "./DispatchBreakdown";

const Tile = ({ icon: Icon, label, value, loading }) => (
  <div className="p-4 rounded-xl bg-slate-50">
    <p className="flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase text-slate-500">
      <Icon className="w-4 h-4" />
      {label}
    </p>
    {loading ? (
      <div className="w-20 h-6 mt-2 rounded animate-pulse bg-slate-200" />
    ) : (
      <p className="mt-1 text-xl font-semibold tabular-nums text-slate-800">{value}</p>
    )}
  </div>
);

// `server` is undefined while the summary endpoint is being asked, null when it isn't
// available, and the summary otherwise. Without it, totals are worked out from each trip's log.
const ClientSummary = ({ label, clientId, trackings }) => {
  const [server, setServer] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    setServer(undefined);
    if (!clientId) {
      setServer(null);
      return undefined;
    }
    LogTrackingService.GetClientSummary(clientId).then((res) => {
      if (!cancelled) setServer(res);
    });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const ids = useMemo(
    () => (server === null ? trackings.map((t) => t.treeFellId).filter(Boolean) : []),
    [server, trackings]
  );
  const breakdown = useDispatchBreakdown(ids);

  const loading = server === undefined || (server === null && breakdown.loading);
  const summary = server
    ? server
    : {
        trips: trackings.length,
        logs: breakdown.pieces,
        totalVolume: breakdown.totalVolume,
        totalCost: breakdown.totalCost,
        compartments: breakdown.compartments,
      };

  return (
    <section className="p-5 bg-white border shadow-sm rounded-2xl border-slate-200/80">
      <div className="flex items-start gap-3">
        <span className="grid w-10 h-10 shrink-0 place-items-center rounded-xl bg-tclPrimary/10 text-tclPrimary">
          <HiOutlineOfficeBuilding className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Client summary</p>
          <h3 className="text-lg font-semibold truncate text-slate-800">{label}</h3>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 lg:grid-cols-4">
        <Tile icon={HiOutlineTruck} label="Trips" value={summary.trips} loading={server === undefined} />
        <Tile icon={HiOutlineCollection} label="Logs received" value={summary.logs} loading={loading} />
        <Tile icon={HiOutlineCube} label="Total volume" value={formatVolume(summary.totalVolume || 0)} loading={loading} />
        <Tile icon={HiOutlineCash} label="Total cost" value={formatMoney(summary.totalCost || 0)} loading={loading} />
      </div>

      <div className="mt-4">
        <p className="flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase text-slate-500">
          <HiOutlineMap className="w-4 h-4" />
          Compartments received from
        </p>
        {loading ? (
          <div className="w-48 h-6 mt-2 rounded animate-pulse bg-slate-100" />
        ) : summary.compartments.length ? (
          <div className="flex flex-wrap gap-2 mt-2">
            {summary.compartments.map((c) => (
              <span
                key={c.compartmentId}
                className="rounded-lg bg-tclPrimary/10 px-2.5 py-1 text-sm font-medium text-tclPrimary"
              >
                {c.compartmentName || `Compartment #${c.compartmentId}`}
                {c.concessionName && <span className="font-normal text-tclPrimary/70"> · {c.concessionName}</span>}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-400">No compartment recorded for these logs.</p>
        )}
      </div>

      {!server && !loading && (breakdown.unpriced > 0 || breakdown.failed > 0 || breakdown.unlocated > 0) && (
        <div className="mt-4 space-y-1">
          {breakdown.failed > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-rose-600">
              <HiOutlineExclamationCircle className="w-3.5 h-3.5 mt-px shrink-0" />
              Couldn't load {breakdown.failed} of this client's logs, so the totals are incomplete.
            </p>
          )}
          {breakdown.unpriced > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600">
              <HiOutlineExclamationCircle className="w-3.5 h-3.5 mt-px shrink-0" />
              {breakdown.unpriced} {breakdown.unpriced === 1 ? "species has" : "species have"} no market value, so the total cost leaves {breakdown.unpriced === 1 ? "it" : "them"} out.
            </p>
          )}
          {breakdown.unlocated > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600">
              <HiOutlineExclamationCircle className="w-3.5 h-3.5 mt-px shrink-0" />
              {breakdown.unlocated} {breakdown.unlocated === 1 ? "species has" : "species have"} no compartment set, so the list may be incomplete.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default ClientSummary;
